/**
 * Phone answer recorder: microphone -> AudioWorklet (or ScriptProcessor / MediaRecorder fallback)
 * -> 16 kHz mono Int16 frames -> silence detector + segmenter -> <= 25 s WAV segments uploaded to
 * POST /api/stt/answer as they close -> transcripts joined by seq in onFinal.
 *
 * Plain factory (not a hook): the caller holds the instance in a ref.
 *
 * Lifecycle
 *   prime()   inside the Start tap: creates the single AudioContext, resumes it, probes the worklet
 *   start()   after TTS ended: acquires the mic (one getUserMedia at a time), attaches the tier, records
 *   stop()    Done: closes the open segment, uploads, then onFinal once every upload settled
 *   cancel()  abandon this answer: no upload of buffered audio, in-flight uploads aborted, no onFinal
 *   release() end of interview / unmount: cancel + close the AudioContext
 *
 * Interruptions (tab hidden, pagehide, track ended/muted, context suspended/interrupted) close and
 * upload what was captured, stop the mic and report onError({ code: "interrupted", recoverable: true }).
 * From there the caller may start() again with the SAME questionKey/applicationId to resume the same
 * answer (seq continues, transcripts merge), stop() to finalise what was captured, or cancel().
 *
 * Half-duplex is the caller's job (cancel()/stop() before TTS, start() after), wake lock is not this
 * module's job. Everything the caller needs to render comes through the callbacks.
 */

import { apiFetchInit } from "@/shared/lib/authStorage";
import workletUrl from "./pcm-capture-processor.worklet.js?url";
import workletSrc from "./pcm-capture-processor.worklet.js?raw";
import {
  FRAME_MS,
  FRAME_SAMPLES,
  Int16Framer,
  LinearDownsampler,
  TARGET_SAMPLE_RATE,
  type PcmFrame,
} from "./pcmDownsampler";
import { assertWav, encodeWavFromInt16Frames } from "./wavEncoder";
import { SilenceDetector } from "./silenceDetector";
import { Segmenter, type CloseReason, type ClosedSegment } from "./segmenter";

export type RecorderStatus =
  | "idle"
  | "priming"
  | "acquiring"
  | "recording"
  | "finishing"
  | "done"
  | "error"
  | "unsupported";

export type RecorderTier = "worklet-url" | "worklet-blob" | "scriptprocessor" | "mediarecorder";

export interface AnswerRecorderCallbacks {
  onStatus?(s: RecorderStatus): void;
  /** ~10 Hz while recording on a PCM tier (not available on the MediaRecorder tier). */
  onLevel?(level01: number, speaking: boolean): void;
  /** First confirmed onset in this answer. */
  onSpeechStart?(): void;
  /** Fired once at 12 s if no speech has armed the detector. */
  onNoSpeechHint?(elapsedMs: number): void;
  onSegmentUploaded?(seq: number, text: string): void;
  onFinal?(r: {
    transcript: string;
    segments: number;
    failedSegments: number;
    durationMs: number;
    tier: RecorderTier;
  }): void;
  /** codes: not-allowed, not-readable, unsupported, upload-failed, interrupted */
  onError?(e: { code: string; message: string; recoverable: boolean }): void;
  /** One line per notable event, for the on-screen diagnostics panel. */
  onDebug?(event: string, extra?: unknown): void;
}

export interface StartOptions {
  /** App BCP-47 code, e.g. "en-IN", "hi-IN"; the server maps it to Sarvam ("unknown" allowed). */
  language: string;
  applicationId: number | string;
  /** <= 64 chars identifying the question, e.g. "opening:2" or "ai:3". */
  questionKey: string;
  /** Trailing silence that ends an armed answer. Default 3000. */
  silenceMs?: number;
  /** Hard cap. Default 90000. */
  maxMs?: number;
}

export interface AnswerRecorder {
  /** getUserMedia + (AudioContext or MediaRecorder) + secure context. */
  isSupported(): boolean;
  /** MUST be called inside a user gesture (the Start tap). */
  prime(): Promise<void>;
  start(opts: StartOptions): Promise<void>;
  stop(): void;
  cancel(): void;
  release(): void;
  getStatus(): RecorderStatus;
  getTier(): RecorderTier | null;
}

export interface AnswerRecorderDeps {
  fetchImpl?: typeof fetch;
  fetchInit?: (init: RequestInit) => RequestInit;
  endpoint?: string;
}

const DEFAULT_ENDPOINT = "/api/stt/answer";
const SS_TIER = "swar_rec_tier_v1";
const SS_DEADTAP_STRIKES = "swar_rec_deadtap_strikes_v1";
const MAX_IN_FLIGHT = 2;
const RETRY_DELAYS_MS = [1000, 2000, 4000];
/** Per-attempt upload deadline. Long enough for a slow 3G upload of an 800 KB
 *  segment, short enough that a dead connection cannot hang the answer. */
const UPLOAD_ATTEMPT_TIMEOUT_MS = 45000;
/** Thresholds for rescuing an answer the speech gate dropped (see
 *  handleClosedSegment). ~-40 dBFS: far above a silent room, far below even
 *  quiet phone-distance speech, so genuine silence is still never uploaded. */
const RESCUE_MIN_MS = 1000;
const RESCUE_MIN_PEAK = 328;

/** Largest absolute sample in a segment, 0..32767. */
function segmentPeak(frames: Int16Array[]): number {
  let peak = 0;
  for (const f of frames) {
    for (let i = 0; i < f.length; i++) {
      const v = f[i] < 0 ? -f[i] : f[i];
      if (v > peak) peak = v;
    }
  }
  return peak;
}
const MAX_RETRY_AFTER_MS = 15000;
const GUM_RETRY_MS = 1000;
const MEDIAREC_ROTATE_MS = 24000;
const MEDIAREC_BITRATE = 32000;
const MEDIAREC_MIMES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4;codecs=mp4a.40.2", "audio/mp4"];
const WALLCLOCK_SLACK_MS = 5000;
const TIERS: readonly RecorderTier[] = ["worklet-url", "worklet-blob", "scriptprocessor", "mediarecorder"];

type WorkletLoad = "url" | "blob" | "none";

/** Error JSON from /api/stt/answer: { error, code?, retryAfterMs? } */
interface ServerErrorBody {
  error?: unknown;
  code?: unknown;
  retryAfterMs?: unknown;
}

interface UploadJob {
  seq: number;
  blob: Blob;
  filename: string;
  durationMs: number;
  isFinal: boolean;
  tier: RecorderTier;
  attempt: number;
  controller: AbortController | null;
}

interface MediaRecSegment {
  rec: MediaRecorder;
  chunks: Blob[];
  startedAt: number;
  isFinal: boolean;
  rotateTimer: ReturnType<typeof setTimeout> | null;
}

interface Answer {
  id: number;
  language: string;
  applicationId: string;
  questionKey: string;
  silenceMs: number;
  maxMs: number;
  segmenter: Segmenter;
  detector: SilenceDetector | null;
  stream: MediaStream | null;
  track: MediaStreamTrack | null;
  /** Tears down the current capture graph (worklet / script processor). */
  tap: (() => void) | null;
  /** Long-lived references for the ScriptProcessor tier (GC safety). */
  spRefs: unknown[];
  mediaRec: MediaRecSegment | null;
  tier: RecorderTier | null;
  results: Map<number, string>;
  failed: number;
  queue: UploadJob[];
  inFlight: Set<UploadJob>;
  /** Producers (MediaRecorder onstop) that will still enqueue an upload. */
  pendingProducers: number;
  capturedMs: number;
  micLive: boolean;
  finalRequested: boolean;
  finalDelivered: boolean;
  cancelled: boolean;
  interrupted: boolean;
  speechStartFired: boolean;
  listenerCleanup: Array<() => void>;
  timers: Array<ReturnType<typeof setTimeout>>;
}

function nowMs(): number {
  return typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function macrotask(): Promise<void> {
  return sleep(0);
}

function getAudioContextCtor(): (typeof AudioContext) | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
  return w.AudioContext || w.webkitAudioContext || null;
}

function getMediaRecorderCtor(): (typeof MediaRecorder) | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as { MediaRecorder?: typeof MediaRecorder };
  return typeof w.MediaRecorder === "function" ? w.MediaRecorder : null;
}

function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent || "");
}

function ssGet(key: string): string | null {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

function ssSet(key: string, value: string | null): void {
  try {
    if (typeof sessionStorage === "undefined") return;
    if (value == null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    /* private mode / blocked storage: tier memory is only an optimisation */
  }
}

function readTierMemory(): RecorderTier | null {
  const v = ssGet(SS_TIER);
  return v && (TIERS as readonly string[]).includes(v) ? (v as RecorderTier) : null;
}

function readStrikes(): number {
  const n = parseInt(ssGet(SS_DEADTAP_STRIKES) || "0", 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function errName(e: unknown): string {
  return e && typeof e === "object" && "name" in e ? String((e as { name?: unknown }).name) : "";
}

function errMessage(e: unknown): string {
  if (e && typeof e === "object" && "message" in e) return String((e as { message?: unknown }).message);
  return String(e);
}

function pickMediaRecorderMime(MR: typeof MediaRecorder): string | null {
  if (typeof MR.isTypeSupported !== "function") return null;
  for (const m of MEDIAREC_MIMES) {
    try {
      if (MR.isTypeSupported(m)) return m;
    } catch {
      /* ignore */
    }
  }
  return null;
}

export function createAnswerRecorder(cb: AnswerRecorderCallbacks, deps: AnswerRecorderDeps = {}): AnswerRecorder {
  const endpoint = deps.endpoint || DEFAULT_ENDPOINT;
  const fetchInit = deps.fetchInit || apiFetchInit;
  const fetchImpl: typeof fetch =
    deps.fetchImpl || ((input, init) => fetch(input, init));

  let status: RecorderStatus = "idle";
  let ctx: AudioContext | null = null;
  let workletLoad: Promise<WorkletLoad> | null = null;
  let workletLoadCtx: AudioContext | null = null;
  let primedTier: RecorderTier | null = readTierMemory();
  let current: Answer | null = null;
  let answerCounter = 0;
  let released = false;

  const debug = (event: string, extra?: unknown): void => {
    try {
      cb.onDebug?.(event, extra);
    } catch {
      /* never let a diagnostics sink break capture */
    }
  };

  const setStatus = (s: RecorderStatus): void => {
    if (status === s) return;
    status = s;
    try {
      cb.onStatus?.(s);
    } catch {
      /* ignore */
    }
  };

  const fail = (code: string, message: string, recoverable: boolean): void => {
    debug("error", { code, message, recoverable });
    try {
      cb.onError?.({ code, message, recoverable });
    } catch {
      /* ignore */
    }
  };

  function isSupported(): boolean {
    if (typeof window === "undefined" || typeof navigator === "undefined") return false;
    const gum = !!(navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === "function");
    const secure = (window as { isSecureContext?: boolean }).isSecureContext !== false;
    return gum && secure && (!!getAudioContextCtor() || !!getMediaRecorderCtor());
  }

  function ensureContext(): AudioContext | null {
    if (ctx && (ctx.state as string) !== "closed") return ctx;
    const Ctor = getAudioContextCtor();
    if (!Ctor) return null;
    try {
      // Default rate on purpose: the worklet downsamples, and the same context can play TTS on iOS.
      ctx = new Ctor();
    } catch (e) {
      debug("audiocontext-error", errMessage(e));
      ctx = null;
    }
    if (ctx) {
      workletLoad = null;
      workletLoadCtx = null;
    }
    return ctx;
  }

  async function resumeContext(c: AudioContext): Promise<void> {
    try {
      if ((c.state as string) !== "running") await c.resume();
    } catch (e) {
      debug("resume-error", errMessage(e));
    }
  }

  function loadWorklet(c: AudioContext): Promise<WorkletLoad> {
    if (workletLoad && workletLoadCtx === c) return workletLoad;
    workletLoadCtx = c;
    workletLoad = (async (): Promise<WorkletLoad> => {
      if (!c.audioWorklet || typeof c.audioWorklet.addModule !== "function") {
        debug("worklet-unavailable");
        return "none";
      }
      try {
        await c.audioWorklet.addModule(workletUrl);
        debug("worklet-loaded", { via: "url" });
        return "url";
      } catch (e1) {
        debug("worklet-url-failed", errMessage(e1));
        if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function" || typeof Blob === "undefined") {
          return "none";
        }
        let blobUrl: string | null = null;
        try {
          blobUrl = URL.createObjectURL(new Blob([workletSrc], { type: "text/javascript" }));
          await c.audioWorklet.addModule(blobUrl);
          debug("worklet-loaded", { via: "blob" });
          return "blob";
        } catch (e2) {
          debug("worklet-blob-failed", errMessage(e2));
          return "none";
        } finally {
          if (blobUrl) {
            try {
              URL.revokeObjectURL(blobUrl);
            } catch {
              /* ignore */
            }
          }
        }
      }
    })();
    return workletLoad;
  }

  // ─── priming ──────────────────────────────────────────────────────────────

  async function prime(): Promise<void> {
    if (released) return;
    if (!isSupported()) {
      setStatus("unsupported");
      return;
    }
    if (status === "acquiring" || status === "recording" || status === "finishing") return;
    setStatus("priming");
    try {
      const c = ensureContext();
      const MR = getMediaRecorderCtor();
      if (!c) {
        primedTier = MR ? "mediarecorder" : null;
        if (!primedTier) setStatus("unsupported");
      } else {
        await resumeContext(c);
        const mem = readTierMemory();
        if (mem === "scriptprocessor" || mem === "mediarecorder") {
          primedTier = mem;
        } else {
          const wl = await loadWorklet(c);
          if (wl === "url") primedTier = "worklet-url";
          else if (wl === "blob") primedTier = "worklet-blob";
          else if (typeof c.createScriptProcessor === "function") primedTier = "scriptprocessor";
          else primedTier = MR ? "mediarecorder" : null;
        }
        if (primedTier) ssSet(SS_TIER, primedTier);
      }
      debug("primed", { tier: primedTier, contextRate: c ? c.sampleRate : null, state: c ? c.state : null });
    } catch (e) {
      debug("prime-error", errMessage(e));
    } finally {
      if (status === "priming") setStatus("idle");
    }
  }

  // ─── microphone ───────────────────────────────────────────────────────────

  function stopStream(stream: MediaStream | null): boolean {
    if (!stream) return false;
    let stopped = false;
    try {
      for (const t of stream.getTracks()) {
        try {
          t.stop();
          stopped = true;
        } catch {
          /* ignore */
        }
      }
    } catch {
      /* ignore */
    }
    return stopped;
  }

  function buildConstraints(): MediaStreamConstraints {
    const audio: MediaTrackConstraints = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    };
    // Plain (ideal) values only. iOS ignores unknown constraints but the scout says not to send
    // channelCount there; Chromium captures at the device rate either way, so never send sampleRate.
    if (!isIOS()) audio.channelCount = 1;
    return { audio };
  }

  async function acquireStream(a: Answer): Promise<MediaStream> {
    let attempt = 0;
    for (;;) {
      try {
        const s = await navigator.mediaDevices.getUserMedia(buildConstraints());
        return s;
      } catch (e) {
        const name = errName(e);
        debug("gum-error", { name, message: errMessage(e), attempt });
        if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") {
          throw { code: "not-allowed", message: "Microphone permission denied", recoverable: true };
        }
        const transient = name === "NotReadableError" || name === "AbortError" || name === "TrackStartError";
        if (transient && attempt === 0 && !a.cancelled) {
          attempt += 1;
          await sleep(GUM_RETRY_MS);
          if (a.cancelled || current !== a) throw { code: "cancelled", message: "cancelled", recoverable: true };
          continue;
        }
        throw { code: "not-readable", message: "Microphone is not available: " + (name || errMessage(e)), recoverable: true };
      }
    }
  }

  // ─── capture tiers ────────────────────────────────────────────────────────

  function attachWorklet(a: Answer, c: AudioContext, stream: MediaStream): void {
    const src = c.createMediaStreamSource(stream);
    const node = new AudioWorkletNode(c, "pcm-capture", {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
      channelCount: 1,
      channelCountMode: "explicit",
      processorOptions: { targetRate: TARGET_SAMPLE_RATE, frameMs: FRAME_MS },
    });
    node.port.onmessage = (e: MessageEvent) => {
      const m = e.data as (PcmFrame & { type: string }) | { type: "ready"; contextRate: number } | undefined;
      if (!m) return;
      if (m.type === "ready") {
        debug("worklet-ready", m);
        return;
      }
      if (m.type === "frame") onPcmFrame(a, m as PcmFrame);
      // 'flush' (< 100 ms remainder after stop) is intentionally dropped: the detector needs
      // full frames and the tail at a Done tap or auto-stop is silence.
    };
    const mute = c.createGain();
    mute.gain.value = 0; // silent path to destination keeps the graph pulled on WebKit
    src.connect(node);
    node.connect(mute);
    mute.connect(c.destination);
    a.tap = () => {
      try {
        node.port.postMessage({ type: "stop" });
      } catch {
        /* ignore */
      }
      node.port.onmessage = null;
      try {
        src.disconnect();
      } catch {
        /* ignore */
      }
      try {
        node.disconnect();
      } catch {
        /* ignore */
      }
      try {
        mute.disconnect();
      } catch {
        /* ignore */
      }
    };
  }

  function attachScriptProcessor(a: Answer, c: AudioContext, stream: MediaStream): void {
    const src = c.createMediaStreamSource(stream);
    const sp = c.createScriptProcessor(4096, 1, 1);
    const ds = new LinearDownsampler(c.sampleRate, TARGET_SAMPLE_RATE);
    const framer = new Int16Framer(FRAME_SAMPLES, (m) => {
      if (m.type === "frame") onPcmFrame(a, m);
    });
    sp.onaudioprocess = (ev: AudioProcessingEvent) => {
      try {
        framer.push(ds.process(ev.inputBuffer.getChannelData(0)));
      } catch {
        /* ignore a malformed buffer */
      }
    };
    const mute = c.createGain();
    mute.gain.value = 0;
    src.connect(sp);
    sp.connect(mute);
    mute.connect(c.destination);
    // The node must stay referenced or onaudioprocess stops firing on some engines.
    a.spRefs = [src, sp, ds, framer, mute];
    a.tap = () => {
      sp.onaudioprocess = null;
      try {
        src.disconnect();
      } catch {
        /* ignore */
      }
      try {
        sp.disconnect();
      } catch {
        /* ignore */
      }
      try {
        mute.disconnect();
      } catch {
        /* ignore */
      }
      a.spRefs = [];
    };
  }

  function startMediaRecorderSegment(a: Answer): void {
    const MR = getMediaRecorderCtor();
    if (!MR || !a.stream) throw new Error("MediaRecorder unavailable");
    const mime = pickMediaRecorderMime(MR);
    const options: MediaRecorderOptions = { audioBitsPerSecond: MEDIAREC_BITRATE };
    if (mime) options.mimeType = mime;
    let rec: MediaRecorder;
    try {
      rec = new MR(a.stream, options);
    } catch {
      rec = new MR(a.stream);
    }
    const seg: MediaRecSegment = { rec, chunks: [], startedAt: nowMs(), isFinal: false, rotateTimer: null };
    rec.ondataavailable = (e: BlobEvent) => {
      if (e.data && e.data.size > 0) seg.chunks.push(e.data);
    };
    rec.onstop = () => {
      const dur = Math.max(0, Math.round(nowMs() - seg.startedAt));
      a.capturedMs += dur;
      const type = rec.mimeType || mime || "audio/webm";
      const blob = new Blob(seg.chunks, { type });
      seg.chunks = [];
      if (!a.cancelled && blob.size > 0) {
        const seq = a.segmenter.allocateSeq();
        const ext = /mp4/i.test(type) ? "m4a" : "webm";
        enqueueUpload(a, {
          seq,
          blob,
          filename: `answer-${seq}.${ext}`,
          durationMs: dur,
          isFinal: seg.isFinal,
          tier: "mediarecorder",
          attempt: 0,
          controller: null,
        });
        debug("segment-closed", { tier: "mediarecorder", seq, durationMs: dur, bytes: blob.size, isFinal: seg.isFinal });
      }
      a.pendingProducers = Math.max(0, a.pendingProducers - 1);
      settle(a);
    };
    rec.onerror = (ev: Event) => {
      debug("mediarecorder-error", errMessage((ev as unknown as { error?: unknown }).error ?? ev));
      interrupt(a, "mediarecorder-error");
    };
    rec.start(); // NO timeslice: chunks are not independently decodable
    debug("mediarecorder-start", { mime: rec.mimeType || mime });
    seg.rotateTimer = setTimeout(() => rotateMediaRecorder(a, seg), MEDIAREC_ROTATE_MS);
    a.timers.push(seg.rotateTimer);
    a.mediaRec = seg;
  }

  function stopMediaRecorderSegment(a: Answer, seg: MediaRecSegment, isFinal: boolean): void {
    if (seg.rotateTimer) {
      clearTimeout(seg.rotateTimer);
      seg.rotateTimer = null;
    }
    seg.isFinal = isFinal;
    if (a.mediaRec === seg) a.mediaRec = null;
    if (seg.rec.state !== "inactive") {
      a.pendingProducers += 1;
      try {
        seg.rec.stop();
      } catch (e) {
        a.pendingProducers = Math.max(0, a.pendingProducers - 1);
        debug("mediarecorder-stop-error", errMessage(e));
      }
    }
  }

  function rotateMediaRecorder(a: Answer, seg: MediaRecSegment): void {
    if (a.cancelled || a.finalRequested || !a.micLive || a.mediaRec !== seg) return;
    stopMediaRecorderSegment(a, seg, false);
    try {
      startMediaRecorderSegment(a);
    } catch (e) {
      debug("mediarecorder-rotate-error", errMessage(e));
      interrupt(a, "mediarecorder-rotate-error");
    }
  }

  function attachMediaRecorderTier(a: Answer): void {
    startMediaRecorderSegment(a);
    a.tier = "mediarecorder";
    // Wall-clock cap: no frames flow on this tier, so the detector cannot enforce maxMs.
    const t = setTimeout(() => {
      if (a.micLive && !a.finalRequested && !a.cancelled) {
        debug("cap", { tier: "mediarecorder" });
        finish(a, "cap");
      }
    }, a.maxMs);
    a.timers.push(t);
  }

  /** Decide the tier for this answer and attach it. Throws { code, message } when nothing works. */
  async function attachCapture(a: Answer): Promise<void> {
    const stream = a.stream;
    if (!stream) throw new Error("no stream");
    const MR = getMediaRecorderCtor();
    const mem = readTierMemory();
    const strikes = readStrikes();
    // After a dead tap we retry the Web Audio tap exactly once (strikes === 1); a second dead tap sticks.
    const forceMR = mem === "mediarecorder" && strikes !== 1;
    const c = forceMR ? null : ensureContext();

    if (c) {
      await resumeContext(c);
      if (a.cancelled || a.finalRequested || current !== a) return;
      try {
        const wl = mem === "scriptprocessor" ? "none" : await loadWorklet(c);
        if (a.cancelled || a.finalRequested || current !== a) return;
        if (wl !== "none") {
          attachWorklet(a, c, stream);
          a.tier = wl === "url" ? "worklet-url" : "worklet-blob";
        } else if (typeof c.createScriptProcessor === "function") {
          attachScriptProcessor(a, c, stream);
          a.tier = "scriptprocessor";
        }
      } catch (e) {
        debug("tap-failed", errMessage(e));
        a.tap?.();
        a.tap = null;
        a.tier = null;
      }
      if (a.tier) {
        a.detector = new SilenceDetector({ silenceMs: a.silenceMs, maxMs: a.maxMs });
        // Wall-clock sanity check for the cap only (frame counts drive every VAD decision).
        const t = setTimeout(() => {
          if (a.micLive && !a.finalRequested && !a.cancelled) {
            debug("cap-wallclock");
            finish(a, "cap");
          }
        }, a.maxMs + WALLCLOCK_SLACK_MS);
        a.timers.push(t);
        const onState = () => {
          const st = c.state as string;
          if (a.micLive && st !== "running" && a.tier !== "mediarecorder") interrupt(a, "context-" + st);
        };
        c.addEventListener("statechange", onState);
        a.listenerCleanup.push(() => c.removeEventListener("statechange", onState));
      }
    }

    if (!a.tier) {
      if (!MR) throw { code: "unsupported", message: "No audio capture path is available in this browser", recoverable: false };
      attachMediaRecorderTier(a);
    }

    if (a.tier && a.tier !== mem) ssSet(SS_TIER, a.tier);
    primedTier = a.tier;
    debug("tier", { tier: a.tier, contextRate: c ? c.sampleRate : null, resumed: a.interrupted });
  }

  function attachInterruptListeners(a: Answer): void {
    const track = a.track;
    if (track) {
      const onEnded = () => interrupt(a, "track-ended");
      const onMute = () => interrupt(a, "track-muted");
      track.addEventListener("ended", onEnded);
      track.addEventListener("mute", onMute);
      a.listenerCleanup.push(() => {
        track.removeEventListener("ended", onEnded);
        track.removeEventListener("mute", onMute);
      });
    }
    if (typeof document !== "undefined") {
      const onVis = () => {
        if (document.visibilityState === "hidden") interrupt(a, "hidden");
      };
      document.addEventListener("visibilitychange", onVis);
      a.listenerCleanup.push(() => document.removeEventListener("visibilitychange", onVis));
    }
    if (typeof window !== "undefined") {
      const onHide = () => interrupt(a, "pagehide");
      window.addEventListener("pagehide", onHide);
      a.listenerCleanup.push(() => window.removeEventListener("pagehide", onHide));
    }
  }

  // ─── frames ───────────────────────────────────────────────────────────────

  function onPcmFrame(a: Answer, m: PcmFrame): void {
    if (a.cancelled || !a.micLive || a.finalRequested || current !== a) return;
    if (!a.detector || a.tier === "mediarecorder") return;
    if (!m.pcm || m.pcm.length === 0) return;
    const r = a.detector.push(m);
    a.capturedMs += FRAME_MS;

    try {
      cb.onLevel?.(r.noSignal ? 0 : r.level01, r.state === "SPEECH");
    } catch {
      /* ignore */
    }
    if (r.onset && !a.speechStartFired) {
      a.speechStartFired = true;
      debug("speech-start", { elapsedMs: r.elapsedMs, floorDb: Math.round(r.floorDb), onDb: Math.round(r.onDb) });
      try {
        cb.onSpeechStart?.();
      } catch {
        /* ignore */
      }
    }
    if (r.noSpeechHint) {
      debug("no-speech-hint", { elapsedMs: r.elapsedMs, floorDb: Math.round(r.floorDb) });
      try {
        cb.onNoSpeechHint?.(r.elapsedMs);
      } catch {
        /* ignore */
      }
    }

    const closed = a.segmenter.push({
      pcm: m.pcm,
      rmsDb: m.rmsDb,
      cls: r.cls,
      speech: r.speech,
      onsetBack: r.onsetBack,
    });
    if (closed) handleClosedSegment(a, closed, false);

    if (r.deadTap) {
      handleDeadTap(a);
      return;
    }
    if (r.autoStop) {
      debug("auto-stop", { elapsedMs: r.elapsedMs, speechMs: r.speechMs, trailingSilenceMs: r.trailingSilenceMs });
      finish(a, "auto-stop");
      return;
    }
    if (r.capReached) {
      debug("cap", { elapsedMs: r.elapsedMs });
      finish(a, "cap");
    }
  }

  function handleDeadTap(a: Answer): void {
    const track = a.track;
    const c = ctx;
    const live = !!track && track.readyState === "live" && track.muted === false;
    const running = !!c && (c.state as string) === "running";
    if (!live || !running) {
      debug("dead-tap-ignored", { live, running });
      return;
    }
    const strikes = readStrikes() + 1;
    ssSet(SS_DEADTAP_STRIKES, String(strikes));
    debug("dead-tap", { strikes, tier: a.tier });
    closeOpenSegment(a, "dead-tap", false);
    a.tap?.();
    a.tap = null;
    a.detector = null;
    const MR = getMediaRecorderCtor();
    if (!MR) {
      interrupt(a, "dead-tap-no-fallback", { code: "unsupported", message: "The microphone produced no audio", recoverable: true });
      return;
    }
    try {
      attachMediaRecorderTier(a);
      ssSet(SS_TIER, "mediarecorder");
      primedTier = "mediarecorder";
      debug("tier", { tier: "mediarecorder", reason: "dead-tap" });
    } catch (e) {
      debug("mediarecorder-attach-error", errMessage(e));
      interrupt(a, "dead-tap-no-fallback", { code: "unsupported", message: "The microphone produced no audio", recoverable: true });
    }
  }

  // ─── segments & uploads ───────────────────────────────────────────────────

  function closeOpenSegment(a: Answer, reason: CloseReason, isFinal: boolean): void {
    const seg = a.segmenter.close(reason);
    if (seg) handleClosedSegment(a, seg, isFinal);
  }

  function handleClosedSegment(a: Answer, seg: ClosedSegment, isFinal: boolean): void {
    let seq = seg.seq;
    if (!seg.upload || seq == null) {
      // Last-resort rescue: this answer is ending having uploaded nothing at
      // all, i.e. the RMS gate decided the candidate never spoke. That gate can
      // be wrong — a noisy room with aggressive AGC can hold the adaptive floor
      // above the speech threshold — and dropping here loses the WHOLE answer.
      // Send the audio and let Sarvam decide whether there are words in it.
      const rescue =
        isFinal &&
        a.segmenter.uploadedCount === 0 &&
        seg.durationMs >= RESCUE_MIN_MS &&
        segmentPeak(seg.frames) >= RESCUE_MIN_PEAK;
      if (!rescue) {
        debug("segment-dropped", { index: seg.index, reason: seg.reason, durationMs: seg.durationMs });
        return;
      }
      seq = a.segmenter.allocateSeq();
      debug("segment-rescued", { index: seg.index, reason: seg.reason, durationMs: seg.durationMs, seq });
    }
    const blob = encodeWavFromInt16Frames(seg.frames, TARGET_SAMPLE_RATE);
    const job: UploadJob = {
      seq,
      blob,
      filename: `answer-${seq}.wav`,
      durationMs: seg.durationMs,
      isFinal,
      tier: a.tier || "worklet-url",
      attempt: 0,
      controller: null,
    };
    debug("segment-closed", {
      seq,
      reason: seg.reason,
      durationMs: seg.durationMs,
      speechMs: seg.speechMs,
      startMs: seg.startMs,
      bytes: blob.size,
      trimmed: [seg.trimmedLeadingMs, seg.trimmedTrailingMs],
      isFinal,
    });
    if (import.meta.env.DEV && typeof blob.arrayBuffer === "function") {
      assertWav(blob).catch((e) => debug("wav-check-failed", errMessage(e)));
    }
    enqueueUpload(a, job);
  }

  function enqueueUpload(a: Answer, job: UploadJob): void {
    if (a.cancelled) return;
    a.queue.push(job);
    pump(a);
  }

  function pump(a: Answer): void {
    while (!a.cancelled && a.inFlight.size < MAX_IN_FLIGHT && a.queue.length > 0) {
      const job = a.queue.shift() as UploadJob;
      void runUpload(a, job);
    }
    settle(a);
  }

  function buildForm(a: Answer, job: UploadJob): FormData {
    const fd = new FormData();
    // Text fields BEFORE the file part (multer sees them in req.body when the file arrives).
    fd.append("language", a.language);
    fd.append("applicationId", a.applicationId);
    fd.append("seq", String(job.seq));
    fd.append("questionKey", a.questionKey);
    fd.append("durationMs", String(Math.round(job.durationMs)));
    fd.append("isFinal", job.isFinal ? "true" : "false");
    fd.append("tier", job.tier);
    fd.append("audio", job.blob, job.filename);
    return fd;
  }

  async function runUpload(a: Answer, job: UploadJob): Promise<void> {
    a.inFlight.add(job);
    try {
      for (;;) {
        if (a.cancelled) return;
        const controller = typeof AbortController === "function" ? new AbortController() : null;
        job.controller = controller;
        debug("upload-start", { seq: job.seq, attempt: job.attempt, bytes: job.blob.size });
        let res: Response | null = null;
        let netErr: unknown = null;
        // Without a deadline a stalled upload (dead 4G, captive portal) never
        // settles: the answer would sit in "Transcribing…" forever and onFinal
        // would never fire. An abort surfaces as a retryable network error.
        const deadline = controller
          ? setTimeout(() => {
              try {
                controller.abort();
              } catch {
                /* already settled */
              }
            }, UPLOAD_ATTEMPT_TIMEOUT_MS)
          : null;
        try {
          const init: RequestInit = { method: "POST", body: buildForm(a, job) };
          if (controller) init.signal = controller.signal;
          res = await fetchImpl(endpoint, fetchInit(init));
        } catch (e) {
          netErr = e;
        } finally {
          if (deadline) clearTimeout(deadline);
        }
        job.controller = null;
        if (a.cancelled) return;

        if (res && res.ok) {
          let data: unknown = null;
          try {
            data = await res.json();
          } catch {
            data = null;
          }
          const text =
            data && typeof data === "object" && typeof (data as { transcript?: unknown }).transcript === "string"
              ? ((data as { transcript: string }).transcript)
              : "";
          a.results.set(job.seq, text);
          debug("upload-ok", { seq: job.seq, chars: text.length });
          try {
            cb.onSegmentUploaded?.(job.seq, text);
          } catch {
            /* ignore */
          }
          return;
        }

        const httpStatus = res ? res.status : 0;
        let body: ServerErrorBody | null = null;
        if (res) {
          try {
            body = (await res.json()) as ServerErrorBody | null;
          } catch {
            body = null;
          }
        }
        // A misconfigured server (no/!valid Sarvam key) will answer identically
        // on every attempt, so retrying just delays the failure by 7 seconds.
        const terminalConfig = Boolean(body && body.code === "stt_misconfigured");
        const retryable = (!res || httpStatus === 429 || httpStatus >= 500) && !terminalConfig;
        if (retryable && job.attempt < RETRY_DELAYS_MS.length) {
          let delay = RETRY_DELAYS_MS[job.attempt];
          job.attempt += 1;
          const ra = body && typeof body.retryAfterMs === "number" ? body.retryAfterMs : 0;
          if (httpStatus === 429 && ra > 0) delay = Math.max(delay, Math.min(ra, MAX_RETRY_AFTER_MS));
          debug("upload-retry", { seq: job.seq, status: httpStatus, error: netErr ? errMessage(netErr) : undefined, delayMs: delay });
          await sleep(delay);
          continue;
        }

        const serverMsg = body && typeof body.error === "string" ? body.error : "";
        const message = res
          ? `Upload failed (${httpStatus})${serverMsg ? ": " + serverMsg : ""}`
          : `Upload failed: ${errMessage(netErr)}`;
        a.failed += 1;
        debug("upload-failed", { seq: job.seq, status: httpStatus, message });
        fail("upload-failed", message, false);
        return;
      }
    } finally {
      a.inFlight.delete(job);
      if (!a.cancelled) pump(a);
    }
  }

  function settle(a: Answer): void {
    if (a.cancelled || a.finalDelivered || !a.finalRequested) return;
    if (a.queue.length > 0 || a.inFlight.size > 0 || a.pendingProducers > 0) return;
    a.finalDelivered = true;
    const transcript = Array.from(a.results.entries())
      .sort((x, y) => x[0] - y[0])
      .map((e) => e[1].trim())
      .filter((t) => t.length > 0)
      .join(" ");
    const result = {
      transcript,
      segments: a.segmenter.uploadedCount,
      failedSegments: a.failed,
      durationMs: Math.round(a.capturedMs),
      tier: (a.tier || primedTier || "worklet-url") as RecorderTier,
    };
    debug("final", { ...result, chars: transcript.length });
    if (current === a) setStatus("done");
    try {
      cb.onFinal?.(result);
    } catch {
      /* ignore */
    }
  }

  // ─── lifecycle transitions ────────────────────────────────────────────────

  function clearTimers(a: Answer): void {
    for (const t of a.timers) clearTimeout(t);
    a.timers = [];
  }

  function teardownMic(a: Answer): void {
    a.micLive = false;
    for (const off of a.listenerCleanup) {
      try {
        off();
      } catch {
        /* ignore */
      }
    }
    a.listenerCleanup = [];
    clearTimers(a);
    if (a.tap) {
      try {
        a.tap();
      } catch {
        /* ignore */
      }
      a.tap = null;
    }
    a.detector = null;
    stopStream(a.stream);
    a.stream = null;
    a.track = null;
  }

  /** Close everything for this answer and let uploads drain into onFinal. */
  function finish(a: Answer, reason: CloseReason): void {
    if (a.cancelled || a.finalRequested) return;
    a.finalRequested = true;
    if (current === a) setStatus("finishing");
    if (a.tier === "mediarecorder") {
      const seg = a.mediaRec;
      if (seg) stopMediaRecorderSegment(a, seg, true);
    } else {
      closeOpenSegment(a, reason, true);
    }
    teardownMic(a);
    debug("finish", { reason, capturedMs: Math.round(a.capturedMs), pending: a.queue.length + a.inFlight.size + a.pendingProducers });
    settle(a);
  }

  function interrupt(
    a: Answer,
    why: string,
    error: { code: string; message: string; recoverable: boolean } = {
      code: "interrupted",
      message: "Recording was interrupted (" + why + ")",
      recoverable: true,
    },
  ): void {
    if (a.cancelled || a.finalRequested || !a.micLive) return;
    debug("interrupted", { why, capturedMs: Math.round(a.capturedMs) });
    if (a.tier === "mediarecorder") {
      const seg = a.mediaRec;
      if (seg) stopMediaRecorderSegment(a, seg, false);
    } else {
      closeOpenSegment(a, "interrupted", false);
    }
    teardownMic(a);
    a.interrupted = true;
    if (current === a) setStatus("error");
    fail(error.code, error.message, error.recoverable);
  }

  function discardAnswer(a: Answer): void {
    a.cancelled = true;
    teardownMic(a);
    if (a.mediaRec) {
      const seg = a.mediaRec;
      a.mediaRec = null;
      if (seg.rotateTimer) clearTimeout(seg.rotateTimer);
      try {
        if (seg.rec.state !== "inactive") seg.rec.stop();
      } catch {
        /* ignore */
      }
    }
    a.queue = [];
    for (const job of a.inFlight) {
      try {
        job.controller?.abort();
      } catch {
        /* ignore */
      }
    }
    a.inFlight.clear();
    a.pendingProducers = 0;
  }

  function newAnswer(opts: StartOptions): Answer {
    return {
      id: ++answerCounter,
      language: opts.language,
      applicationId: String(opts.applicationId),
      questionKey: opts.questionKey.slice(0, 64),
      silenceMs: opts.silenceMs ?? 3000,
      maxMs: opts.maxMs ?? 90000,
      segmenter: new Segmenter(),
      detector: null,
      stream: null,
      track: null,
      tap: null,
      spRefs: [],
      mediaRec: null,
      tier: null,
      results: new Map(),
      failed: 0,
      queue: [],
      inFlight: new Set(),
      pendingProducers: 0,
      capturedMs: 0,
      micLive: false,
      finalRequested: false,
      finalDelivered: false,
      cancelled: false,
      interrupted: false,
      speechStartFired: false,
      listenerCleanup: [],
      timers: [],
    };
  }

  async function start(opts: StartOptions): Promise<void> {
    if (released) return;
    if (!isSupported()) {
      setStatus("unsupported");
      fail("unsupported", "This browser cannot record audio here (needs HTTPS and a microphone API)", false);
      return;
    }

    let waitMacrotask = false;
    let a: Answer;
    const prev = current;
    const canResume =
      !!prev &&
      prev.interrupted &&
      !prev.cancelled &&
      !prev.finalRequested &&
      prev.questionKey === opts.questionKey.slice(0, 64) &&
      prev.applicationId === String(opts.applicationId);
    if (prev && canResume) {
      a = prev;
      a.interrupted = false;
      a.language = opts.language;
      if (opts.silenceMs != null) a.silenceMs = opts.silenceMs;
      if (opts.maxMs != null) a.maxMs = opts.maxMs;
      a.tier = null;
      // Per-attempt UI state: let onSpeechStart fire again so the resumed
      // attempt re-clears the "we can't hear you" hint when speech returns.
      a.speechStartFired = false;
      debug("resume", { questionKey: a.questionKey, nextSeq: a.segmenter.uploadedCount });
    } else {
      if (prev && !prev.cancelled && !prev.finalDelivered) {
        // Tolerate start() while a previous answer is live: abandon it (no onFinal) and release the mic.
        const hadMic = prev.micLive;
        debug("start-abandons-previous", { prevStatus: status, hadMic });
        discardAnswer(prev);
        waitMacrotask = waitMacrotask || hadMic;
      } else if (prev && prev.micLive) {
        stopStream(prev.stream);
        waitMacrotask = true;
      }
      a = newAnswer(opts);
    }
    current = a;
    setStatus("acquiring");
    debug("start", { questionKey: a.questionKey, language: a.language, silenceMs: a.silenceMs, maxMs: a.maxMs });

    if (waitMacrotask) await macrotask(); // never overlap two getUserMedia captures (iOS mutes the first)
    if (a.cancelled || current !== a) return;

    const c = ensureContext();
    if (c) await resumeContext(c);
    if (a.cancelled || current !== a) return;

    try {
      const stream = await acquireStream(a);
      if (a.cancelled || a.finalRequested || current !== a) {
        stopStream(stream);
        return;
      }
      a.stream = stream;
      a.track = stream.getAudioTracks()[0] || null;
      a.micLive = true;
      debug("gum-ok", {
        label: a.track ? a.track.label : null,
        settings: a.track && typeof a.track.getSettings === "function" ? a.track.getSettings() : null,
      });
      await attachCapture(a);
      if (a.cancelled || a.finalRequested || current !== a) {
        teardownMic(a);
        return;
      }
      if (!a.tier) {
        // start() was superseded while attaching; nothing else to do
        return;
      }
      attachInterruptListeners(a);
      setStatus("recording");
    } catch (e) {
      const err = e as { code?: string; message?: string; recoverable?: boolean };
      teardownMic(a);
      if (err && err.code === "cancelled") return;
      if (current === a) setStatus("error");
      const code = err && typeof err.code === "string" ? err.code : "not-readable";
      const message = err && typeof err.message === "string" ? err.message : errMessage(e);
      fail(code, message, err && typeof err.recoverable === "boolean" ? err.recoverable : true);
    }
  }

  function stop(): void {
    const a = current;
    if (!a || a.cancelled || a.finalDelivered) return;
    if (a.finalRequested) return;
    if (a.micLive) {
      debug("done-tap", { capturedMs: Math.round(a.capturedMs) });
      finish(a, "done");
      return;
    }
    // Interrupted (or still acquiring): finalise whatever was captured / uploaded so far.
    a.finalRequested = true;
    setStatus("finishing");
    debug("finish", { reason: "done-after-interrupt", pending: a.queue.length + a.inFlight.size + a.pendingProducers });
    settle(a);
  }

  function cancel(): void {
    const a = current;
    if (!a) return;
    if (a.cancelled) return;
    debug("cancel", { status, capturedMs: Math.round(a.capturedMs) });
    discardAnswer(a);
    current = null;
    setStatus("idle");
  }

  function release(): void {
    cancel();
    released = true;
    debug("release");
    const c = ctx;
    ctx = null;
    workletLoad = null;
    workletLoadCtx = null;
    if (c) {
      try {
        void c.close().catch(() => {});
      } catch {
        /* ignore */
      }
    }
    setStatus("idle");
  }

  return {
    isSupported,
    prime,
    start,
    stop,
    cancel,
    release,
    getStatus: () => status,
    getTier: () => (current && current.tier) || primedTier,
  };
}
